import type { ComponentType } from 'react';

import {
  CaseStudiesIcon,
  ExamPlannerIcon,
  FlashcardsIcon,
  ImageOcclusionIcon,
  MindMapsIcon,
  OralExamIcon,
  QuizzesIcon,
  SpacedRepetitionIcon,
  SummariesIcon,
  TranscriberIcon,
} from './landing-icons';

export type LandingGroupId = 'create' | 'plan' | 'study';

export type LandingFeature = {
  id: string;
  /** Whole i18n keys (greppable): `landingV3.features.<id>.badge|title|subtitle`. */
  badgeKey: string;
  titleKey: string;
  subtitleKey: string;
  /** Bottom-nav step group (Figma node 6:2144: 1 Create · 2 Plan · 3 Study). */
  group: LandingGroupId;
  icon: ComponentType<{ className?: string }>;
  /** Icon chip tint: `-100` fill + `-400` border (auto-inverting DS ramps). */
  chipClass: string;
  /** Icon stroke tint (Figma-exact ramp shade per feature). */
  iconClass: string;
  /** Feature badge tint: `-50` fill + `-100` border + `-600` text. */
  badgeClass: string;
  /** Figma's tinted chip drop-shadow (kept literal — shadows don't invert). */
  shadowClass: string;
  /** Hero scatter placement: chip center as % of the 1512×982 Figma frame. */
  hero: { cx: number; cy: number; rotate: number };
};

/**
 * Section + bottom-nav order, straight from the Figma nav pills (node 6:2144):
 * Create → Flashcards, Mind maps, Transcriber, Summaries, Image occlusion;
 * Plan → Exam planner; Study → Spaced repetition, Quizzes, Oral, Case studies.
 */
export const LANDING_FEATURES: LandingFeature[] = [
  {
    id: 'flashcards',
    badgeKey: 'landingV3.features.flashcards.badge',
    titleKey: 'landingV3.features.flashcards.title',
    subtitleKey: 'landingV3.features.flashcards.subtitle',
    group: 'create',
    icon: FlashcardsIcon,
    chipClass: 'bg-primary-100 border-primary-400',
    iconClass: 'text-primary-700',
    badgeClass: 'bg-primary-50 border-primary-100 text-primary-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(142,81,255,0.2)]',
    hero: { cx: 25.58, cy: 20.23, rotate: -9.21 },
  },
  {
    id: 'mindMaps',
    badgeKey: 'landingV3.features.mindMaps.badge',
    titleKey: 'landingV3.features.mindMaps.title',
    subtitleKey: 'landingV3.features.mindMaps.subtitle',
    group: 'create',
    icon: MindMapsIcon,
    chipClass: 'bg-tertiary-100 border-tertiary-400',
    iconClass: 'text-tertiary-600',
    badgeClass: 'bg-tertiary-50 border-tertiary-100 text-tertiary-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(21,93,252,0.2)]',
    hero: { cx: 12.84, cy: 31.68, rotate: -9.93 },
  },
  {
    id: 'transcriber',
    badgeKey: 'landingV3.features.transcriber.badge',
    titleKey: 'landingV3.features.transcriber.title',
    subtitleKey: 'landingV3.features.transcriber.subtitle',
    group: 'create',
    icon: TranscriberIcon,
    chipClass: 'bg-orange-100 border-orange-400',
    iconClass: 'text-orange-600',
    badgeClass: 'bg-orange-50 border-orange-100 text-orange-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(255,137,4,0.3)]',
    hero: { cx: 87.25, cy: 64.89, rotate: 8.03 },
  },
  {
    id: 'summaries',
    badgeKey: 'landingV3.features.summaries.badge',
    titleKey: 'landingV3.features.summaries.title',
    subtitleKey: 'landingV3.features.summaries.subtitle',
    group: 'create',
    icon: SummariesIcon,
    chipClass: 'bg-rose-100 border-rose-400',
    iconClass: 'text-rose-500',
    badgeClass: 'bg-rose-50 border-rose-100 text-rose-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(251,44,54,0.2)]',
    hero: { cx: 12.8, cy: 51.99, rotate: -2.69 },
  },
  {
    id: 'imageOcclusion',
    badgeKey: 'landingV3.features.imageOcclusion.badge',
    titleKey: 'landingV3.features.imageOcclusion.title',
    subtitleKey: 'landingV3.features.imageOcclusion.subtitle',
    group: 'create',
    icon: ImageOcclusionIcon,
    chipClass: 'bg-lime-100 border-lime-400',
    iconClass: 'text-lime-700',
    badgeClass: 'bg-lime-50 border-lime-100 text-lime-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(94,165,0,0.2)]',
    hero: { cx: 91.43, cy: 44.85, rotate: 8.43 },
  },
  {
    id: 'examPlanner',
    badgeKey: 'landingV3.features.examPlanner.badge',
    titleKey: 'landingV3.features.examPlanner.title',
    subtitleKey: 'landingV3.features.examPlanner.subtitle',
    group: 'plan',
    icon: ExamPlannerIcon,
    chipClass: 'bg-sky-100 border-sky-400',
    iconClass: 'text-sky-700',
    badgeClass: 'bg-sky-50 border-sky-100 text-sky-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(0,105,168,0.2)]',
    hero: { cx: 75.71, cy: 21.05, rotate: 16.24 },
  },
  {
    id: 'spacedRepetition',
    badgeKey: 'landingV3.features.spacedRepetition.badge',
    titleKey: 'landingV3.features.spacedRepetition.title',
    subtitleKey: 'landingV3.features.spacedRepetition.subtitle',
    group: 'study',
    icon: SpacedRepetitionIcon,
    chipClass: 'bg-fuchsia-100 border-fuchsia-400',
    iconClass: 'text-fuchsia-600',
    badgeClass: 'bg-fuchsia-50 border-fuchsia-100 text-fuchsia-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(142,81,255,0.3)]',
    hero: { cx: 24.7, cy: 82.9, rotate: -15 },
  },
  {
    id: 'quizzes',
    badgeKey: 'landingV3.features.quizzes.badge',
    titleKey: 'landingV3.features.quizzes.title',
    subtitleKey: 'landingV3.features.quizzes.subtitle',
    group: 'study',
    icon: QuizzesIcon,
    chipClass: 'bg-green-100 border-green-400',
    iconClass: 'text-green-700',
    badgeClass: 'bg-green-50 border-green-100 text-green-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(0,201,80,0.2)]',
    hero: { cx: 11.44, cy: 71.59, rotate: -14.55 },
  },
  {
    id: 'oralExam',
    badgeKey: 'landingV3.features.oralExam.badge',
    titleKey: 'landingV3.features.oralExam.title',
    subtitleKey: 'landingV3.features.oralExam.subtitle',
    group: 'study',
    icon: OralExamIcon,
    chipClass: 'bg-amber-100 border-amber-400',
    iconClass: 'text-amber-700',
    badgeClass: 'bg-amber-50 border-amber-100 text-amber-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(255,185,0,0.2)]',
    hero: { cx: 86.45, cy: 30.06, rotate: 12.55 },
  },
  {
    id: 'caseStudies',
    badgeKey: 'landingV3.features.caseStudies.badge',
    titleKey: 'landingV3.features.caseStudies.title',
    subtitleKey: 'landingV3.features.caseStudies.subtitle',
    group: 'study',
    icon: CaseStudiesIcon,
    chipClass: 'bg-pink-100 border-pink-400',
    iconClass: 'text-pink-700',
    badgeClass: 'bg-pink-50 border-pink-100 text-pink-600',
    shadowClass: 'drop-shadow-[0px_2px_8px_rgba(246,51,154,0.3)]',
    hero: { cx: 81.11, cy: 83.34, rotate: 24.77 },
  },
];

/** Nav groups in display order, with the features each pill contains. */
export const LANDING_GROUPS: { id: LandingGroupId; features: LandingFeature[] }[] = (
  ['create', 'plan', 'study'] as const
).map((id) => ({ id, features: LANDING_FEATURES.filter((f) => f.group === id) }));

/** Index of a feature in the section order (drives scroll targets). */
export const featureIndex = (id: string) =>
  LANDING_FEATURES.findIndex((f) => f.id === id);

/**
 * How much scroll (in viewport-heights) each beat of the page occupies. Cards
 * get well under a viewport each so a throw resolves quickly — the gesture
 * should feel like flicking a card away, not dragging a timeline.
 */
export const LANDING_SPANS = {
  /** Hero + the chip gather (the only long scroll-linked stretch). Half a
   *  viewport: the gather and the cut to Ka's line run twice as fast as they
   *  first did — a full screen of scroll for them read as slow. */
  hero: 0.5,
  /** The one Professor Ka interstitial before the deck (play-once, see the page). */
  ka: 0.9,
  /** Each card throw — scroll-linked. 0.55, then 0.8, let a flick of the
   *  wheel throw several cards, so the deck was hard to stop on (user
   *  2026-10-09): a card now takes a screen and a half of scroll. */
  card: 1.6,
  /** How long the LAST card sits at rest before the stage releases. It is
   *  never thrown (user 2026-10-09): it stays where it is and scrolls up with
   *  the stage, and the agent chat follows it from below — the page below
   *  never rises behind a card that is still on screen. */
  outro: 0.3,
} as const;

/** How long the deck takes to rise into view before card 0's throw (Figma
 *  12:242), in viewport-heights. */
export const DECK_RISE_SPAN = 0.4;

/**
 * The page's scroll timeline in viewport-heights. Sequence: hero/gather →
 * the ONE Ka interstitial ("First, upload everything you have…") → the
 * feature cards thrown off a single deck, Create → Plan → Study without a
 * break, the LAST one staying put → the stage releases and scrolls up with it.
 * Everything after — the agent chat, social proof, leaderboard, closing CTA,
 * FAQ — is NORMAL flow below the runway. `cardThrowStart[g]` is where card g
 * sits readable on top of the deck and its throw begins (the last card's
 * never does); `kaStarts[0]` opens the interstitial (kept as a list: the stage
 * machine reads it that way).
 */
export const LANDING_TIMELINE = (() => {
  let at: number = LANDING_SPANS.hero;
  const kaStarts = [at];
  at += LANDING_SPANS.ka;
  const cardThrowStart = LANDING_FEATURES.map((_, g) => at + g * LANDING_SPANS.card);
  // The last card is not thrown: it holds for the outro, then leaves with
  // the stage.
  at = cardThrowStart[cardThrowStart.length - 1];
  const runwayEnd = at + LANDING_SPANS.outro;
  /** The stage releases: from here the page below scrolls into view and Ka
   *  belongs to it. */
  const handover = runwayEnd;
  return { kaStarts, cardThrowStart, handover, runwayEnd, totalSteps: runwayEnd };
})();

/** Order chips pop in during the intro — scattered, not clockwise. */
export const INTRO_POP_ORDER = [
  'flashcards',
  'oralExam',
  'summaries',
  'examPlanner',
  'quizzes',
  'transcriber',
  'mindMaps',
  'caseStudies',
  'spacedRepetition',
  'imageOcclusion',
];
